import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Input, Toast } from 'antd-mobile'
import {
  rocketChatLogin,
  getRocketChatMessages,
  getRocketChatRooms,
  getChannelThreadMessages,
  getChatThreadMessages,
  getRocketMessagesReceipt,
  deleteRocketChatMessage,
  getPinnedMessages,
  updateRocketChatMessage,
  pinRocketChatMessage,
  unpinRocketChatMessage,
  uploadFile,
  sendMessageNotification,
  searchRocketChatMessages,
  getRocketChatMessageById,
  sendRocketChatMessage,
  getMe
} from '../../api'
import { toBase64, setTokenItem, saveUser, loadUser, getToken, getCachedRoomsData, setCachedRoomsData } from '../../utils'
import ChatHeader from './ChatHeader'
import ChannelMessages from './ChannelMessages'
import ThreadView from './ThreadView'
import ChatInput from './ChatInput'
import ThreadListWindow from './ThreadListWindow'
import './styles/ChannelMessages.css'
import './styles/ChannelPage.css'
import PinnedMessages from './PinnedMessages'
import SearchResults from './Searchresults'
import ChannelSearchBar from './ChannelSearchBar'
import IosSpinner from './IosSpinner'
import MembersModal from './MembersModal'

const ChannelPage = () => {
  const { channelId } = useParams()
  const navigate = useNavigate()

  const location = useLocation()
  const [messages, setMessages] = useState([])
  const [threads, setThreads] = useState({})
  const [selectedThread, setSelectedThread] = useState(null)
  const [loading, setLoading] = useState(true)
  const [input, setInput] = useState('')
  const [threadInput, setThreadInput] = useState('')
  const [channelName, setChannelName] = useState('')
  const [showThreadsWindow, setShowThreadsWindow] = useState(false)
  const [channelThreads, setChannelThreads] = useState([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadUnreadCount, setThreadUnreadCount] = useState(
    location.state?.channelThreadUnreadCount || 0
  )
  const [unreadCount, setUnreadCount] = useState(location.state?.unreadCount || 0)
  const channelNameHeader = location.state?.channelName || ''
  const clientAvatars = location?.state?.userAvatars
  const [userAvatarsState, setUserAvatarsState] = useState(clientAvatars || [])
  const ws = useRef(null)
  const messageQueue = useRef([])

  const [rocketChatData, setRocketChatData] = useState(() => {
    const data = localStorage.getItem('rocket-chat-data')
    return data ? JSON.parse(data) : null
  })

  const [person, setPerson] = useState(() => loadUser())
  const [enteredFromThreadsWindow, setEnteredFromThreadsWindow] = useState(false)
  const setReadReceipts = location.state?.setReadReceipts || (() => { })
  const initialReadReceipts = location.state?.readReceipts || []
  const [redReceipts, setRedReceipts] = useState(initialReadReceipts)
  const [showPinnedModal, setShowPinnedModal] = useState(false)
  const [pinnedMessages, setPinnedMessages] = useState([])
  const [loadingPinned, setLoadingPinned] = useState(false)
  const [editingMessage, setEditingMessage] = useState(null)
  const [editingThreadMessage, setEditingThreadMessage] = useState(null)
  const [users, setUsers] = useState([])
  const [dmUsers, setDmUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [showUserSuggestions, setShowUserSuggestions] = useState(false)
  const [threadFilteredUsers, setThreadFilteredUsers] = useState([])
  const [showThreadUserSuggestions, setShowThreadUserSuggestions] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchTriggered, setSearchTriggered] = useState(false)
  const [jumpToMessageId, setJumpToMessageId] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const selectedThreadRef = useRef(selectedThread)
  const showThreadsWindowRef = useRef(showThreadsWindow)
  const [loadingThreadReplies, setLoadingThreadReplies] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [offset, setOffset] = useState(20)
  const [scrollCheckEnabled, setScrollCheckEnabled] = useState(true)
  const [isJumping, setIsJumping] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [hasMoreNextMessages, setHasMoreNextMessages] = useState(true)
  const [triggerScroll, setTriggerScroll] = useState(false)
  const [feedbacks, setFeedbacks] = useState([])
  const [isDm, setIsDm] = useState(location.state?.isDm || false)
  const lastScrollTop = useRef(0)
  const limit = 100
  const reconnectAttempts = useRef(0)
  const reconnectTimer = useRef(null)
  const isInitialLoad = useRef(true)
  const loadingMoreRef = useRef(false)
  const lastFetchTimeRef = useRef(0)
  const FETCH_THROTTLE_DELAY = 2000
  const suppressBottomFetch = useRef(false)
  const hasMoreNextMessagesRef = useRef(true)
  const isInitialMount = useRef(true)
  const [showMembersModal, setShowMembersModal] = useState(false)
  const [hasUpdatedBadge, setHasUpdatedBadge] = useState(false)

  const scheduleReconnect = () => {
    if (reconnectTimer.current) return
    const delay = reconnectAttempts.current === 0 ? 100 : Math.min(1000 * (reconnectAttempts.current + 1), 30000)
    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null
      reconnectAttempts.current += 1
      connectWebSocket()
    }, delay)
  }

  const refreshPerson = async (token) => {
    setTokenItem(token)
    const { person: newPerson } = await getMe()
    setPerson(newPerson)
    saveUser(newPerson)

    const rocketData = await rocketChatLogin(newPerson.id)
    setRocketChatData(rocketData)
    localStorage.setItem('rocket-chat-data', JSON.stringify(rocketData))
  }

  const fetchData = async () => {
    if (!person?.id) return
    setLoading(true)
    isInitialMount.current = true

    try {
      let matchedChannel = location?.state?.channelData
      let channelUsers = []
      let dmUsers = []

      // Always try to get rooms data for user mentions and channel validation
      let roomsRes = getCachedRoomsData()

      // Fetch fresh data if no valid cache
      if (!roomsRes) {
        roomsRes = await getRocketChatRooms({
          person_id: person.id,
          client_id: person.id
        })

        // Cache the rooms data
        setCachedRoomsData(roomsRes)
      }

      // If we don't have channelData from navigation, get it from rooms data
      if (!matchedChannel && roomsRes) {
        matchedChannel = roomsRes.rooms.find(r => r._id === channelId)
        setUserAvatarsState(roomsRes?.userAvatars || [])
      } else if (matchedChannel) {
        if (roomsRes) {
          setUserAvatarsState(roomsRes?.userAvatars || [])
        }
      }
      if (matchedChannel) {
        channelUsers = matchedChannel?.users || []
        dmUsers = matchedChannel?.usernames || []
        setUsers(channelUsers)
        setDmUsers(dmUsers)
        let displayName = 'Unknown'
        const isDMChannel = matchedChannel.t === 'd'
        if (isDMChannel && Array.isArray(matchedChannel.usernames)) {
          const myUsername = `${person.first_name}_${person.id}`
          displayName =
            matchedChannel.usernames.find(u => u !== myUsername) ||
            matchedChannel.usernames[0] ||
            'DM'
          if (displayName.includes('_')) {
            displayName = displayName.split('_')[0]
          }
        } else {
          displayName = matchedChannel.name || matchedChannel.fname || 'Unknown'
          if (displayName.includes('_')) {
            displayName = displayName.split('_')[0]
          }
        }
        setChannelName(displayName)
      }

      // Update isDm state based on determined channel type
      if (matchedChannel) {
        const isDMChannel = matchedChannel.t === 'd'
        setIsDm(isDMChannel)
      }
      const channelIsDm = matchedChannel ? matchedChannel.t === 'd' : false

      let receiptsRes = []
      try {
        receiptsRes = await getRocketMessagesReceipt({
          personId: person.id,
          channels: [channelId]
        })
        if (receiptsRes.length > 0) {
          setRedReceipts(receiptsRes)
        }
      } catch (error) {
        console.warn('[ChannelPage] Failed to fetch read receipts:', error)
      }

      const msgRes = await getRocketChatMessages({
        personId: person.id,
        roomId: channelId,
        viewerId: person.id,
        isDm: channelIsDm,
        count: limit,
        ts: new Date(),
        next: false,
        inclusive: false
      })

      if (msgRes.success) {
        const allMsgs = msgRes.messages.reverse()
        setMessages(allMsgs.filter(msg => !msg.tmid))

        const threadsObj = {}
        allMsgs
          .filter(msg => msg.tmid)
          .forEach(msg => {
            if (!threadsObj[msg.tmid]) threadsObj[msg.tmid] = []
            threadsObj[msg.tmid].push(msg)
          })
        setThreads(threadsObj)

        const feedbackData = allMsgs
          .filter(msg => msg.feedback)
          .map(msg => ({
            message_id: msg._id,
            feedback: msg.feedback
          }))
        setFeedbacks(feedbackData)
      }
    } catch (err) {
      Toast.show({
        content: 'Failed to load channel',
        icon: 'fail',
        duration: 3000
      })
    } finally {
      setLoading(false)
      isInitialMount.current = false
    }
  }

  useEffect(() => {
    if (person?.id) {
      fetchData()
    }
  }, [channelId, person])

  useEffect(() => {
    window.handleMessageFromNative = refreshPerson

    if (navigator.userAgent.includes('life.apeiron.ios')) {
      const hasToken = !!getToken()
      const hasUser = !!loadUser()
      const hasRocketData = !!rocketChatData

      if (!hasToken || !hasUser || !hasRocketData) {
        try {
          window.webkit?.messageHandlers?.loadUser?.postMessage({})
        } catch (err) {
          console.error('🚨 Failed to request token from Swift in ChannelPage:', err)
        }
      }
    }
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
          connectWebSocket()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [channelId])

  const connectWebSocket = () => {

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      return
    }
    ws.current = new WebSocket(process.env.REACT_APP_ROCKET_CHAT_WS_URL)

    ws.current.onopen = () => {
      if (ws.current.readyState === WebSocket.OPEN) {
        reconnectAttempts.current = 0 // reset on success
        ws.current.send(JSON.stringify({ msg: 'connect', version: '1', support: ['1'] }))
      } else {
        console.warn('[WS] Connection not fully open, readyState:', ws.current.readyState)
      }
    }

    ws.current.onmessage = async (event) => {
      const data = JSON.parse(event.data)

      if (!rocketChatData) return

      if (data.msg === 'connected') {
        ws.current.send(
          JSON.stringify({
            msg: 'method',
            method: 'login',
            id: '1',
            params: [{ resume: rocketChatData.authToken }]
          })
        )
      }

      if (data.msg === 'ping') {
        ws.current.send(JSON.stringify({ msg: 'pong' }))
      }

      if (data.msg === 'result' && data.id === '1') {
        if (data.error) {
          console.warn('Login failed in ChannelPage:', data.error)

          try {
            const newData = await rocketChatLogin(person.id)
            localStorage.setItem('rocket-chat-data', JSON.stringify(newData))
            rocketChatData.authToken = newData.authToken

            ws.current.send(
              JSON.stringify({
                msg: 'method',
                method: 'login',
                id: '1',
                params: [{ resume: rocketChatData.authToken }]
              })
            )
          } catch (err) {
            Toast.show({
              content: 'Login to chat server failed. Please reload.',
              icon: 'fail',
              duration: 3000
            })
            return
          }
          return
        }

        ws.current.send(
          JSON.stringify({
            msg: 'sub',
            id: '2',
            name: 'stream-room-messages',
            params: [channelId, false]
          })
        )

        // Update badge for normal navigation
        if (location.state?.unreadCount !== undefined) {
          if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.updateBadge) {
            window.webkit.messageHandlers.updateBadge.postMessage(location.state.unreadCount)
            setHasUpdatedBadge(true)
          }
        }

        // Delay marking as read for push notifications to allow unread divider to show
        const delay = location.state?.unreadCount !== undefined ? 0 : 2000
        setTimeout(() => {
          sendReadMessages(channelId)
        }, delay)
        flushMessageQueue()
        if (!isInitialLoad.current) {
          fetchData()
        } else {
          isInitialLoad.current = false
        }
      }

      if (data.msg === 'changed' && data.collection === 'stream-room-messages') {
        const msg = data.fields.args[0]
        msg['ts'] = msg['ts']['$date']

        if (msg.tmid) {
          setThreads(prev => {
            const oldReplies = prev[msg.tmid] || []

            const updatedReplies = oldReplies.some(m => String(m._id) === String(msg._id))
              ? oldReplies.map(m => (String(m._id) === String(msg._id) ? { ...m, ...msg } : m))
              : [...oldReplies, msg]

            updatedReplies.sort((a, b) => new Date(a.ts) - new Date(b.ts))

            return {
              ...prev,
              [msg.tmid]: updatedReplies
            }
          })

          if (selectedThreadRef.current && selectedThreadRef.current._id === msg.tmid) {
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(
                JSON.stringify({
                  msg: 'method',
                  method: 'readThreads',
                  id: `read-thread-${msg.tmid}`,
                  params: [msg.tmid]
                })
              )
            }
            setRedReceipts(prev =>
              prev.map(r =>
                r.rid === channelId
                  ? { ...r, tunread: r.tunread.filter(id => id !== msg.tmid) }
                  : r
              )
            )
          }
        } else {
          setMessages(prev => {
            const exists = prev.find(m => m._id === msg._id)
            if (exists) {
              return prev.map(m => (m._id === msg._id ? { ...m, ...msg } : m))
            } else {
              const latestMessage = prev.length > 0 ? prev[prev.length - 1] : null
              if (!latestMessage || new Date(msg.ts) > new Date(latestMessage.ts)) {
                return [...prev, msg]
              }
              return prev
            }
          })
        }

        if (msg.u?._id !== person.id && msg.u?._id !== rocketChatData?.userId) {
          getRocketMessagesReceipt({
            personId: person.id,
            channels: [channelId]
          }).then(receipts => {
            const receipt = receipts[0]
            setThreadUnreadCount(Array.isArray(receipt?.tunread) ? receipt.tunread.length : 0)
            setRedReceipts(receipts)
          }).catch(error => {
            console.warn('[ChannelPage] Failed to fetch read receipts for real-time update:', error)
          })
        }

        if (showThreadsWindowRef.current) {
          getChannelThreadMessages({
            roomId: channelId,
            personId: person.id
          }).then(res => {
            const threads = Array.isArray(res?.update) ? res.update : []
            setChannelThreads(threads)
          })
        }
      }
    }

    ws.current.onclose = (e) => {
      console.warn('[WS] Connection closed. Reason:', e.reason)
      scheduleReconnect()
    }

    ws.current.onerror = (e) => {
      console.error('[WS] WebSocket error', e)
      ws.current.close()
    }

  }

  useEffect(() => {
    if (rocketChatData) {
      connectWebSocket()
    }
    return () => ws.current?.close()
    }, [channelId, rocketChatData])

  useEffect(() => {
    showThreadsWindowRef.current = showThreadsWindow
  }, [showThreadsWindow])

  useEffect(() => {
    const receipt = redReceipts.find(r => r.rid === channelId)
    if (receipt) {
      const newUnreadCount = receipt.unread || 0
      setThreadUnreadCount(Array.isArray(receipt?.tunread) ? receipt.tunread.length : 0)
      setUnreadCount(newUnreadCount)

      // Update badge whenever there are unread messages (for background/foreground transitions)
      if (newUnreadCount > 0) {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.updateBadge) {
          window.webkit.messageHandlers.updateBadge.postMessage(newUnreadCount)
          setHasUpdatedBadge(true)
        }
      }
    }
  }, [redReceipts, channelId, hasUpdatedBadge])

  useEffect(() => {
    selectedThreadRef.current = selectedThread
  }, [selectedThread])

  useEffect(() => {
    const wrapper = document.querySelector('.channel-messages-wrapper')
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll)
      return () => wrapper.removeEventListener('scroll', handleScroll)
    }
  }, [messages, loadingMore])

  const handleScroll = async (e) => {
    if (isInitialMount.current) return
    const target = e.target
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 10
    if (!messages.length) return
    if (loadingMoreRef.current) return
    if (suppressBottomFetch.current) return

    if (target.scrollTop === 0 && !loadingMoreRef.current && hasMoreMessages) {
      loadingMoreRef.current = true
      setLoadingMore(true)
      await fetchMoreMessages()
      loadingMoreRef.current = false
      setLoadingMore(false)
    }

    if (
      !isInitialMount.current &&
      scrollCheckEnabled &&
      isAtBottom &&
      !loadingMoreRef.current &&
      hasMoreNextMessages
    ) {
      const now = Date.now()
      if (now - lastFetchTimeRef.current >= FETCH_THROTTLE_DELAY) {
        lastFetchTimeRef.current = now
        loadingMoreRef.current = true
        setLoadingMore(true)
        await fetchNewerMessages()
        loadingMoreRef.current = false
        setLoadingMore(false)
      }
    }
  }

  const fetchNewerMessages = async () => {
    setJumpToMessageId(null)
    if (!messages.length || !hasMoreNextMessagesRef.current) return
    try {
      const lastMsg = messages[messages.length - 1]

      const res = await getRocketChatMessages({
        personId: person.id,
        roomId: channelId,
        viewerId: person.id,
        isDm,
        count: limit,
        ts: lastMsg.ts,
        next: true,
        inclusive: false
      })
      if (res.success) {
        const newerMsgs = res.messages
          .reverse()
          .filter(msg => !msg.tmid)
        if (!newerMsgs.length) {
          setHasMoreNextMessages(false)
          hasMoreNextMessagesRef.current = false
          return
        }
        setMessages(prev => [...prev, ...newerMsgs])
      }
    } catch (err) {
      Toast.show({ content: 'Failed to load newer messages', icon: 'fail' })
    }
  }

  const fetchMoreMessages = async () => {
    setJumpToMessageId(null)
    if (!messages.length || !hasMoreMessages) {
      return
    }
    try {
      const wrapper = document.querySelector('.channel-messages-wrapper')
      const prevScrollHeight = wrapper ? wrapper.scrollHeight : 0

      const res = await getRocketChatMessages({
        personId: person.id,
        roomId: channelId,
        viewerId: person.id,
        isDm,
        count: limit,
        ts: messages[0].ts,
        next: false,
        inclusive: false
      })
      if (res.success) {
        const moreMsgs = res.messages.reverse().filter(msg => !msg.tmid)
              if (!moreMsgs.length) {
        setHasMoreMessages(false)
        return
      }
        setMessages(prev => [...moreMsgs, ...prev])
        setOffset(prev => prev + res.messages.length)

        setTimeout(() => {
          if (wrapper) {
            const newScrollHeight = wrapper.scrollHeight
            wrapper.scrollTop = Math.max(newScrollHeight - prevScrollHeight, 4)
          }
        }, 0)
      }
    } catch (err) {
      Toast.show({ content: 'Failed to load more messages', icon: 'fail' })
    }
  }

  const fetchAroundMessages = async (timestamp) => {
    setScrollCheckEnabled(false)
    setIsJumping(true)
    suppressBottomFetch.current = true

    try {
      const params = {
        roomId: channelId,
        personId: person.id,
        isDm,
        ts: timestamp,
        count: limit,
        next: false,
        inclusive: true,
        viewerId: person.id
      }

      const resPrev = await getRocketChatMessages(params)
      const resNext = await getRocketChatMessages({ ...params, next: true })
      const filteredPrev = resPrev.messages
        .reverse()
        .filter(msg => (!msg.t || msg.t !== 'au') && !msg.tmid && msg.t !== 'message_pinned')
      const filteredNext = (isDm ? resNext.messages : resNext.messages.reverse())
        .filter(msg => (!msg.t || msg.t !== 'au') && !msg.tmid && msg.t !== 'message_pinned')

      setMessages([...filteredPrev, ...filteredNext.splice(1)])
      setHasMoreNextMessages(true)
      hasMoreNextMessagesRef.current = true
    } catch (error) {
      console.error('Error in fetchAroundMessages', error)
    } finally {
      setIsJumping(false)
      setScrollCheckEnabled(true)

      // Allow normal scroll detection again
      setTimeout(() => {
        suppressBottomFetch.current = false
        const wrapper = document.querySelector('.channel-messages-wrapper')
        if (wrapper) {
          const isNearBottom = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 50
          if (isNearBottom) {
            fetchNewerMessages()
          }
        }
      }, 300)
    }
  }

  const flushMessageQueue = () => {
    while (messageQueue.current.length > 0 && ws.current.readyState === WebSocket.OPEN) {
      const msg = messageQueue.current.shift()
      ws.current.send(JSON.stringify(msg))
    }
  }

  const sendReadMessages = channelId => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(
        JSON.stringify({
          msg: 'method',
          method: 'readMessages',
          id: 'read',
          params: [channelId]
        })
      )
    }
  }

  function isHtml(str) {
    return /<\/?[a-z][\s\S]*>/i.test(str)
  }

  const handleSend = async (text, threadRoot = null) => {
    if (!text.trim()) return

    // Clear input immediately for better UX - user sees instant feedback
    if (!threadRoot) {
      setInput('')
    } else {
      setThreadInput('')
    }

    const textWithMentions = replaceMentionsWithUsernames(text, users)

    if (editingMessage) {
      let newTextToSend = text.trim()
      // Convert display names back to usernames for edited messages
      newTextToSend = replaceMentionsWithUsernames(newTextToSend, users)
      if (editingMessage.isHtml && editingMessage.originalHtml) {
        if (editingMessage.originalHtml.includes('<img')) {
          newTextToSend = replaceMentionsWithUsernames(text.trim(), users)
        } else {
          const parser = new DOMParser()
          const doc = parser.parseFromString(editingMessage.originalHtml, 'text/html')
          doc.body.textContent = replaceMentionsWithUsernames(text.trim(), users)
          newTextToSend = doc.body.innerHTML
        }
      }
      try {
        const res = await updateRocketChatMessage({
          message_id: editingMessage._id,
          room_id: channelId,
          person_id: person.id,
          new_text: newTextToSend
        })
        if (res.success) {
          Toast.show({ content: 'Message updated successfully', icon: 'success' })
          const convertedText = replaceMentionsWithUsernames(text.trim(), users)
          setMessages(prev =>
            prev.map(m =>
              m._id === editingMessage._id ? { ...m, msg: convertedText } : m
            )
          )
          setThreads(prev => {
            const updated = { ...prev }
            Object.keys(updated).forEach(tmid => {
              updated[tmid] = updated[tmid].map(msg =>
                msg._id === editingMessage._id ? { ...msg, msg: convertedText } : msg
              )
            })
            return updated
          })
          try {
            await sendMessageNotification({
              client_id: person.id,
              text: text.trim(),
              ...(isDm
                ? {
                  is_DM: true,
                  receiver_id: (() => {
                    const myUsername = `${person.first_name}_${person.id}`
                    const otherUsername = dmUsers.find(u => u !== myUsername)
                    return otherUsername && otherUsername.includes('_')
                      ? Number(otherUsername.split('_')[1])
                      : undefined
                  })()
                }
                : {})
            })
          } catch (err) {
          }
        } else {
          Toast.show({ content: 'Update failed', icon: 'fail' })
        }
      } catch (err) {
        Toast.show({ content: 'Update failed', icon: 'fail' })
      }
      setEditingMessage(null)
      setEditingThreadMessage(null)
      setShowUserSuggestions(false)
      setShowThreadUserSuggestions(false)
      return
    }

    const msgObj = {
      msg: 'method',
      method: 'sendMessage',
      id: `${Date.now()}`,
      params: [
        {
          rid: channelId,
          msg: textWithMentions.trim(),
          ...(threadRoot ? { tmid: threadRoot._id } : {})
        }
      ]
    }

    try {
      await sendRocketChatMessage({
        sender_id: person.id,
        room_id: channelId,
        message: textWithMentions.trim(),
        ...(threadRoot ? { tmid: threadRoot._id } : {})
      })
    } catch (error) {
      Toast.show({
        content: 'Failed to send message. Please check your connection.',
        icon: 'fail',
        duration: 3000
      })
    }

    try {
      await sendMessageNotification({
        client_id: person.id,
        text: text.trim(),
        ...(isDm
          ? {
            is_DM: true,
            receiver_id: (() => {
              const myUsername = `${person.first_name}_${person.id}`
              const otherUsername = dmUsers.find(u => u !== myUsername)
              return otherUsername && otherUsername.includes('_')
                ? Number(otherUsername.split('_')[1])
                : undefined
            })()
          }
          : {})
      })
    } catch (err) {
    }

    setTriggerScroll(true)

    if (!threadRoot) {
      setShowUserSuggestions(false)
    } else {
      setShowThreadUserSuggestions(false)
    }
  }

  const handleImageUpload = async (file) => {
    try {
      let imageUrl

      if (file.type === 'image/svg+xml') {
        imageUrl = await toBase64(file)
      } else {
        const res = await uploadFile({ personId: person.id, file })
        imageUrl = res.url || res.data?.url
      }

      const imageHtml = `<p><img class="ql-image" src="${imageUrl}" /></p>`
      if (selectedThread) {
        await handleSend(imageHtml, selectedThread)
      } else {
        await handleSend(imageHtml)
      }
    } catch (err) {
      
      Toast.show({ content: 'Image upload failed', icon: 'fail' })
    }
  }

  const handleMentionInputChange = (val, setInputFn, setFilteredUsersFn, setShowSuggestionsFn) => {
    setInputFn(val)
    const atIndex = val.lastIndexOf('@')
    if (atIndex !== -1) {
      let afterAt = val.slice(atIndex + 1)
      if (afterAt.includes(' ')) {
        setShowSuggestionsFn(false)
        return
      }
      const originalAfterAt = afterAt  // Store original before splitting
      afterAt = afterAt.split(/\s/)[0]
      const query = afterAt.toLowerCase()
      const exactMatch = users.some(u =>
        u.username?.toLowerCase() === query || u.name?.toLowerCase() === query
      )
      if (exactMatch && !originalAfterAt.includes(' ')) {
        setShowSuggestionsFn(false)
        return
      }
      if (query.length === 0) {
        setFilteredUsersFn(users)
        setShowSuggestionsFn(true)
        return
      }
      const filtered = users.filter(u =>
        u.name?.toLowerCase().includes(query) ||
        u.username?.toLowerCase().includes(query)
      )
      setFilteredUsersFn(filtered)
      setShowSuggestionsFn(filtered.length > 0)
    } else {
      setShowSuggestionsFn(false)
    }
  }

  function replaceMentionsWithUsernames(text, users) {
    let newText = text
    users.forEach(user => {
      const nameRegex = new RegExp(`@${user.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'g')
      newText = newText.replace(nameRegex, `@${user.username}`)
    })
    return newText
  }

  function replaceUsernamesWithNames(text, users) {
    let newText = text
    users.forEach(user => {
      const usernameRegex = new RegExp(`@${user.username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'g')
      newText = newText.replace(usernameRegex, `@${user.name}`)
    })
    return newText
  }

  const handleMentionUserSelect = (user, inputVal, setInputFn, setShowSuggestionsFn) => {
    const val = inputVal
    const atIndex = val.lastIndexOf('@')
    if (atIndex !== -1) {
      const beforeAt = val.slice(0, atIndex)
      const afterAt = val.slice(atIndex + 1)
      const parts = afterAt.split(/\s(.+)/)
      const restOfSentence = parts[1] || ''
      const newVal = `${beforeAt}@${user.name} ${restOfSentence}`
      setInputFn(newVal)
      setShowSuggestionsFn(false)
    }
  }

  const handleInputChange = (val) => {
    handleMentionInputChange(val, setInput, setFilteredUsers, setShowUserSuggestions)
  }

  const handleUserSelect = (user) => {
    handleMentionUserSelect(user, input, setInput, setShowUserSuggestions)
  }

  const handleThreadInputChange = (val) => {
    handleMentionInputChange(val, setThreadInput, setThreadFilteredUsers, setShowThreadUserSuggestions)
  }

  const handleThreadUserSelect = (user) => {
    handleMentionUserSelect(user, threadInput, setThreadInput, setShowThreadUserSuggestions)
  }
  const handleViewThreads = async () => {

    setThreadsLoading(true)
    try {
      let receipts = []
      try {
        receipts = await getRocketMessagesReceipt({
          personId: person.id,
          channels: [channelId]
        })
      } catch (error) {
        console.warn('[ChannelPage] Failed to fetch read receipts for threads view:', error)
      }
      setRedReceipts(receipts)

      setShowThreadsWindow(true)
      const res = await getChannelThreadMessages({
        roomId: channelId,
        personId: person.id
      })
      const threads = Array.isArray(res?.update) ? res.update : []
      setChannelThreads(threads)
    } catch (err) {
      Toast.show({
        content: 'Failed to load threads',
        icon: 'fail',
        duration: 3000
      })
      setChannelThreads([])
    } finally {
      setThreadsLoading(false)
    }
  }

  const handleViewThreadReplies = async (threadRootMsg) => {
    setSelectedThread(threadRootMsg)
    setShowThreadsWindow(false)

    const unreadThreadIds =
      redReceipts.find(r => r.rid === channelId)?.tunread || []

    const wasUnread = unreadThreadIds.includes(threadRootMsg._id)

    setRedReceipts(prev =>
      prev.map(r =>
        r.rid === channelId
          ? { ...r, tunread: Array.isArray(r.tunread) ? r.tunread.filter(id => id !== threadRootMsg._id) : [] }
          : r
      )
    )

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(
        JSON.stringify({
          msg: 'method',
          method: 'readThreads',
          id: 'read-thread-root',
          params: [threadRootMsg._id]
        })
      )
      if (wasUnread) {
        setThreadUnreadCount(prev => Math.max(prev - 1, 0))
      }
    }

    setLoadingThreadReplies(true)
    try {
      const res = await getChatThreadMessages({
        roomId: channelId,
        personId: person.id,
        threadId: threadRootMsg._id
      })
      setThreads(prev => ({
        ...prev,
        [threadRootMsg._id]: res.messages || []
      }))
    } catch (err) {
      Toast.show({ content: 'Failed to load thread replies', icon: 'fail' })
    } finally {
      setLoadingThreadReplies(false)
    }
  }

  const handleViewThreadRepliesFromThreadsWindow = (threadRootMsg) => {
    setEnteredFromThreadsWindow(true)
    handleViewThreadReplies(threadRootMsg)
  }

  const handleViewThreadRepliesFromChannel = (threadRootMsg) => {
    navigate('/chat/' + channelId + '/' + threadRootMsg._id, {
      state: location.state
    })
    const wrapper = document.querySelector('.channel-messages-wrapper')
    if (wrapper) lastScrollTop.current = wrapper.scrollTop
    setEnteredFromThreadsWindow(false)
    handleViewThreadReplies(threadRootMsg)
  }

  const handleShowPinnedMessages = async () => {
    setLoadingPinned(true)
    try {
      const res = await getPinnedMessages({
        roomId: channelId,
        personId: person.id
      })
      setPinnedMessages(res.messages || [])
      setShowPinnedModal(true)
    } catch (err) {
      Toast.show({ content: 'Failed to load pinned messages', icon: 'fail' })
      setPinnedMessages([])
      setShowPinnedModal(true)
    } finally {
      setLoadingPinned(false)
    }
  }

  const handleEditMessage = (msg) => {
    const htmlFormat = isHtml(msg.msg)
    let plainText = htmlFormat
      ? (() => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(msg.msg, 'text/html')
        return doc.body.textContent || ''
      })()
      : msg.msg

    plainText = replaceUsernamesWithNames(plainText, users)

    setEditingMessage({
      ...msg,
      isHtml: htmlFormat,
      originalHtml: htmlFormat ? msg.msg : null
    })

    if (selectedThread) {
      setEditingThreadMessage(msg)
      setThreadInput(plainText)
    } else {
      setInput(plainText)
    }
  }

  const handleDeleteMessage = async ({ messageId, roomId }) => {
    try {
      const res = await deleteRocketChatMessage({
        messageId,
        roomId,
        personId: person.id
      })
      if (res.success) {
        Toast.show({ content: 'Message deleted', icon: 'success' })
        // Remove from channel messages
        setMessages(prev => prev.filter(msg => msg._id !== messageId))
        // Remove from threads (if it's a thread reply)
        setThreads(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(tmid => {
            updated[tmid] = updated[tmid].filter(msg => msg._id !== messageId)
          })
          return updated
        })
      } else {
        Toast.show({ content: res.data.message || 'Delete failed', icon: 'fail' })
      }
    } catch (err) {
      Toast.show({ content: err?.response?.data?.message || 'Delete failed', icon: 'fail' })
    }
  }

  const handlePinMessage = async (msg, pinned) => {
    try {
      const pinOrUnpinMesssages = pinned ? unpinRocketChatMessage : pinRocketChatMessage

      const res = await pinOrUnpinMesssages({
        message_id: msg._id,
        person_id: person.id
      })

      if (res.success) {
        Toast.show({
          content: pinned ? 'Message unpinned' : 'Message pinned',
          icon: 'success'
        })

        setMessages(prev =>
          prev.map(m =>
            m._id === msg._id ? { ...m, pinned: !pinned } : m
          )
        )
        setThreads(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(tmid => {
            updated[tmid] = updated[tmid].map(m =>
              m._id === msg._id ? { ...m, pinned: !pinned } : m
            )
          })
          return updated
        })

      } else {
        Toast.show({ content: 'Action failed', icon: 'fail' })
      }
    } catch (err) {
      Toast.show({ content: 'Action failed', icon: 'fail' })
    }
  }

  const handleSearchClick = async () => {
    setSearchTriggered(true)
    setSearchLoading(true)
    if (!searchQuery.trim()) {
      setSearchResults([])
      setSearchLoading(false)
      Toast.clear() // Hide toast
      return
    }
    try {
      const res = await searchRocketChatMessages({
        roomId: channelId,
        searchText: searchQuery,
        personId: person.id
      })
      setSearchResults(res.messages || [])
    } catch (err) {
      setSearchResults([])
      Toast.show({ content: 'Search failed', icon: 'fail' })
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSearchResultClick = async (msgId) => {
    const msg = searchResults.find(m => m._id === msgId)
    if (!msg) return

    setShowSearch(false)
    setSearchQuery('')
    setIsJumping(true)

    // Add a small delay to ensure state updates
    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      if (msg.tmid) {
        let threadRootMsg = messages.find(m => m._id === msg.tmid)

        if (!threadRootMsg) {
          const res = await getRocketChatMessageById({
            messageId: msg.tmid,
            personId: person.id
          })
          threadRootMsg = res.message
        }

        if (threadRootMsg) {
          setSelectedThread(threadRootMsg)

          // Wait for state update
          await new Promise(resolve => setTimeout(resolve, 50))

          const repliesRes = await getChatThreadMessages({
            roomId: channelId,
            personId: person.id,
            threadId: threadRootMsg._id
          })

          setThreads(prev => ({
            ...prev,
            [threadRootMsg._id]: repliesRes.messages
          }))

          // Wait for threads to be set
          await new Promise(resolve => setTimeout(resolve, 100))

          setIsJumping(false)
          setJumpToMessageId(msg._id)
        }
      } else {
        await fetchAroundMessages(msg.ts)
        // Wait for messages to load and render
        await new Promise(resolve => setTimeout(resolve, 200))
        setJumpToMessageId(msgId)
      }
    } catch (error) {
      setIsJumping(false)
    }
  }

  if (loading) return <IosSpinner />
  if (isJumping) return <IosSpinner />

  return (
    <div>
      {!selectedThread && (
        <>
          {!showSearch ? (
            <ChatHeader
              title={channelNameHeader || channelName}
              onBack={() => {
                navigate('/chat', { state: { refresh: true } })
              }}
              onViewThreads={handleViewThreads}
              threadUnreadCount={threadUnreadCount}
              onShowPinnedMessages={handleShowPinnedMessages}
              search={() => {
                setShowSearch(true)
                setSearchResults([])
                setSearchTriggered(false)
                setSearchQuery('')
              }}
              onShowMembers={!isDm ? () => setShowMembersModal(true) : undefined}
            />
          ) : (
            <ChannelSearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSearchClick={handleSearchClick}
              onClose={() => {
                setShowSearch(false)
                setSearchQuery('')
                setSearchResults([])
                setSearchTriggered(false)
              }}
            />
          )}
        </>
      )}

      {showThreadsWindow && (
        <ThreadListWindow
          threads={channelThreads}
          onClose={() => setShowThreadsWindow(false)}
          onSelectThread={handleViewThreadRepliesFromThreadsWindow}
          readReceipts={redReceipts}
          channelId={channelId}
        />
      )}

      {showPinnedModal && (
        <>
          {loadingPinned && <IosSpinner />}
          <PinnedMessages
            messages={pinnedMessages}
            onClose={() => {
              setLoadingPinned(false)
              setShowPinnedModal(false)
            }}
            userAvatars={clientAvatars}
          />
        </>
      )}
      {showUserSuggestions && (
        <div className="user-suggestion-popup">
          {filteredUsers.map(user => (
            <div
              key={user._id}
              className="user-suggestion-item"
              onClick={() => handleUserSelect(user)}
            >
              {user.name || user.username}
            </div>
          ))}
        </div>
      )}
      {showMembersModal && (
        <MembersModal
          members={users}
          onClose={() => setShowMembersModal(false)}
          userAvatars={clientAvatars}
        />
      )}
      {loadingThreadReplies && <IosSpinner />}
      {!selectedThread ? (
        showSearch && searchTriggered ? (
          <div className="search-results-list">
            {searchLoading ? (
              <IosSpinner />
            ) : (
              <SearchResults
                searchResults={searchResults}
                handleSearchResultClick={handleSearchResultClick}
                searchQuery={searchQuery}
                searchLoading={searchLoading}
              />
            )}
          </div>
        ) : (
          <>
            <ChannelMessages
              messages={messages}
              threads={threads}
              onReplyClick={handleViewThreadRepliesFromChannel}
              onViewThreadReplies={handleViewThreadRepliesFromChannel}
              unreadCount={unreadCount}
              onDeleteMessage={handleDeleteMessage}
              currentUserId={rocketChatData?.userId}
              onEditMessage={handleEditMessage}
              editingMessageId={editingMessage?._id}
              onPinMessage={handlePinMessage}
              searchQuery={''}
              jumpToMessageId={jumpToMessageId}
              loadingMore={loadingMore}
              users={users}
              userAvatars={userAvatarsState}
              triggerScroll={triggerScroll}
              clientId={person.id}
              feedbacks={feedbacks}
            />
            <ChatInput
              value={input}
              onChange={handleInputChange}
              onSend={() => handleSend(input)}
              disabled={!input.trim()}
              isEditing={!!editingMessage}
              onCancelEdit={() => {
                setEditingMessage(null)
                setInput('')
                setShowUserSuggestions(false)
              }}
              onImageUpload={handleImageUpload}
            />
          </>
        )
      ) : (
        <ThreadView
          selectedThread={selectedThread}
          replies={threads[selectedThread._id]}
          userAvatars={userAvatarsState}
          jumpToMessageId={jumpToMessageId} // <-- pass this
          onBack={() => {
            setEditingThreadMessage(null)
            setEditingMessage(null)
            setThreadInput('')
            setInput('')
            setSelectedThread(null)
            setShowThreadsWindow(enteredFromThreadsWindow)
            setMessages(prev => [...prev])

            setTimeout(() => {
              const wrapper = document.querySelector('.channel-messages-wrapper')
              if (wrapper) {
                wrapper.scrollTop = lastScrollTop.current

                setTimeout(() => {
                  if (wrapper.scrollTop === 0) {
                    wrapper.dispatchEvent(new Event('scroll'))
                  }
                }, 100)
              }
            }, 0)

            if (enteredFromThreadsWindow) {
              // Refetch latest thread list when returning
              getChannelThreadMessages({
                roomId: channelId,
                personId: person.id
              }).then(res => {
                const threads = Array.isArray(res?.update) ? res.update : []
                setChannelThreads(threads)
                setShowThreadsWindow(true)
              }).catch(() => {
                setChannelThreads([])
                setShowThreadsWindow(true)
              })
            }
          }}
          threadInput={threadInput}
          setThreadInput={setThreadInput}
          onSendReply={() => {
            handleSend(threadInput, selectedThread)
            setThreadInput('')
          }}
          onDeleteMessage={handleDeleteMessage}
          onEditMessage={handleEditMessage}
          onPinMessage={handlePinMessage}
          currentUserId={rocketChatData?.userId}
          isEditing={!!editingThreadMessage}
          editingMessageId={editingThreadMessage?._id}
          setEditingThreadMessage={setEditingThreadMessage}
          onCancelEdit={() => {
            setEditingThreadMessage(null)
            setEditingMessage(null)
            setThreadInput('')
            setShowThreadUserSuggestions(false)
          }}
          handleThreadInputChange={handleThreadInputChange}
          showThreadUserSuggestions={showThreadUserSuggestions}
          threadFilteredUsers={threadFilteredUsers}
          handleThreadUserSelect={handleThreadUserSelect}
          onImageUpload={handleImageUpload}
        />
      )}
    </div>
  )
}

export default ChannelPage
